export const AUTH_STORAGE_KEYS = {
  token: 'token',
  user: 'user',
  tokenExpiresAt: 'tokenExpiresAt'
} as const;

export const SESSION_WARNING_BEFORE_MS = 2 * 60 * 1000;
export const MAX_TIMEOUT_MS = 2_147_000_000;

export type UnauthorizedReason = 'expired' | 'unauthorized';
export type UnauthorizedHandler = (reason: UnauthorizedReason) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export class ApiAuthError extends Error {
  reason: UnauthorizedReason;
  status?: number;

  constructor(reason: UnauthorizedReason, message: string, status?: number) {
    super(message);
    this.name = 'ApiAuthError';
    this.reason = reason;
    this.status = status;
  }
}

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler;
};

export const getStoredToken = (): string | null => localStorage.getItem(AUTH_STORAGE_KEYS.token);

const getJwtExpiresAt = (token: string): number | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalized = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = JSON.parse(window.atob(normalized));
    const exp = Number(decoded?.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
};

export const getTokenExpiresAt = (): number | null => {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  const value = raw ? Number(raw) : NaN;
  if (Number.isFinite(value)) return value;

  const token = getStoredToken();
  return token ? getJwtExpiresAt(token) : null;
};

export const isTokenExpired = (): boolean => {
  const expiresAt = getTokenExpiresAt();
  return expiresAt !== null && Date.now() >= expiresAt;
};

export const clearStoredSession = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.token);
  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  localStorage.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
};

export const storeSession = (token: string, tokenExpiresMs?: number) => {
  localStorage.setItem(AUTH_STORAGE_KEYS.token, token);

  if (typeof tokenExpiresMs === 'number' && Number.isFinite(tokenExpiresMs)) {
    localStorage.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, String(Date.now() + tokenExpiresMs));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  }
};

export const isApiAuthError = (err: unknown): err is ApiAuthError => err instanceof ApiAuthError;

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = getStoredToken();

  if (token && isTokenExpired()) {
    unauthorizedHandler?.('expired');
    throw new ApiAuthError('expired', 'TOKEN_EXPIRED');
  }

  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(input, { ...init, headers });

  if (response.status === 401 || response.status === 403) {
    unauthorizedHandler?.('unauthorized');
    throw new ApiAuthError('unauthorized', `UNAUTHORIZED_${response.status}`, response.status);
  }

  return response;
}
